import React, { useRef, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/fmp.css';
import '../styles/Das.css';

const PRINT_STYLE = `
  @page { size: A4 landscape; margin: 8mm; }
  @media print {
    .no-print { display: none !important; }
    .sidebar { display: none !important; }
    .content { margin-left: 0 !important; }
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

const fmtCurrency = (val) => {
  const n = parseFloat(val);
  if (isNaN(n)) return '-';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Normaliza severidade removendo acentos e uppercase
const normSev = (s) => (s || '').toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const BADGE_SEV = { GRAVE: 'bg-danger', MEDIO: 'bg-warning text-dark', BAIXO: 'bg-info text-dark' };
const ICON_SEV  = { GRAVE: 'fa-exclamation-circle', MEDIO: 'fa-exclamation-triangle', BAIXO: 'fa-info-circle' };
const COR_SEV   = { GRAVE: '#dc3545', MEDIO: '#ffc107', BAIXO: '#0dcaf0' };

function DivergenciasTable({ rows, prefix, conferencias, onConferencia, onResolucao }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="table-responsive">
      <table className="table table-sm table-hover mb-0 align-middle" style={{ fontSize: '0.93rem' }}>
        <thead>
          <tr style={{ background: '#e8edf5', fontSize: '0.82rem', fontWeight: 700, color: '#2c3e6b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            <th style={{ width: 100 }}>Severidade</th>
            <th style={{ width: 55 }}>Item</th>
            <th>Categoria</th>
            <th style={{ width: 70 }}>Cód.</th>
            <th>Conta</th>
            <th className="text-end" style={{ width: 130 }}>Valor</th>
            <th>Descrição</th>
            <th>Recomendação</th>
            <th style={{ width: 200 }}>Conferência</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d, i) => {
            const key = `${prefix}_${i}`;
            const conf = conferencias[key];
            const confirmado = conf?.resposta === 'Confirmado';
            const incorreto  = conf?.resposta === 'Contestado';
            const faltaResolucao = (confirmado || incorreto) && !(conf?.resolucao || '').trim();
            return (
              <tr key={i} style={confirmado ? { background: '#f0fff4' } : incorreto ? { background: '#fff8f8' } : {}}>
                <td>
                  <span className={`badge ${BADGE_SEV[normSev(d.severidade)] || 'bg-secondary'}`}>
                    <i className={`fas ${ICON_SEV[normSev(d.severidade)] || 'fa-circle'} me-1`}></i>
                    {d.severidade}
                  </span>
                </td>
                <td className="text-center fw-bold text-muted">{d.item_checklist}</td>
                <td><span className="badge bg-light text-dark border">{d.categoria || '-'}</span></td>
                <td className="text-center font-monospace text-muted">{d.codigo || '-'}</td>
                <td><strong>{d.conta || '-'}</strong></td>
                <td className="text-end font-monospace">{d.valor != null ? `R$ ${fmtCurrency(d.valor)}` : '-'}</td>
                <td style={{ minWidth: 200, whiteSpace: 'normal', lineHeight: 1.5 }}>{d.descricao || '-'}</td>
                <td className="text-muted fst-italic" style={{ minWidth: 180, whiteSpace: 'normal', lineHeight: 1.5 }}>{d.recomendacao || '-'}</td>
                <td style={{ minWidth: 320, verticalAlign: 'top', paddingTop: 8 }}>
                  <div className="d-flex gap-1 mb-1">
                    <button
                      className={`btn btn-sm py-0 px-2 ${confirmado ? 'btn-success' : 'btn-outline-success'}`}
                      style={{ fontSize: '0.82rem' }}
                      title="Auditoria correta"
                      onClick={() => onConferencia(key, confirmado ? null : 'Confirmado')}
                    >
                      <i className="fas fa-check me-1"></i>Confirmado
                    </button>
                    <button
                      className={`btn btn-sm py-0 px-2 ${incorreto ? 'btn-danger' : 'btn-outline-danger'}`}
                      style={{ fontSize: '0.82rem' }}
                      title="Auditoria incorreta"
                      onClick={() => onConferencia(key, incorreto ? null : 'Contestado')}
                    >
                      <i className="fas fa-times me-1"></i>Contestado
                    </button>
                  </div>
                  {(confirmado || incorreto) && (
                    <div style={{
                      marginTop: 8,
                      border: `1.5px solid ${faltaResolucao ? '#dc3545' : confirmado ? '#198754' : '#dc3545'}`,
                      borderRadius: 8,
                      overflow: 'hidden',
                      background: '#fff',
                      boxShadow: `0 2px 8px ${confirmado ? 'rgba(25,135,84,0.10)' : 'rgba(220,53,69,0.10)'}`,
                    }}>
                      <div style={{
                        background: confirmado
                          ? 'linear-gradient(90deg, #198754 0%, #157347 100%)'
                          : 'linear-gradient(90deg, #dc3545 0%, #c0392b 100%)',
                        padding: '4px 10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}>
                        <i className={`fas ${confirmado ? 'fa-clipboard-check' : 'fa-clipboard-list'}`} style={{ color: '#fff', fontSize: '0.80rem' }}></i>
                        <span style={{ color: '#fff', fontSize: '0.80rem', fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                          {confirmado ? 'Observação / Plano de resolução' : 'Motivo da contestação'} *
                        </span>
                      </div>
                      <textarea
                        rows={3}
                        placeholder={confirmado
                          ? 'Descreva como esta pendência será resolvida ou adicione uma observação...'
                          : 'Explique por que este alerta está sendo contestado...'}
                        style={{
                          width: '100%',
                          border: 'none',
                          outline: 'none',
                          padding: '8px 10px',
                          fontSize: '0.91rem',
                          lineHeight: 1.6,
                          resize: 'vertical',
                          background: '#fff',
                          color: '#212529',
                          minHeight: 72,
                        }}
                        value={conf?.resolucao || ''}
                        onChange={(e) => onResolucao(key, e.target.value)}
                      />
                      {faltaResolucao ? (
                        <div style={{
                          padding: '3px 10px',
                          background: '#fff5f5',
                          borderTop: '1px solid #f5c6cb',
                          fontSize: '0.70rem',
                          color: '#dc3545',
                          fontWeight: 600,
                          textAlign: 'right',
                        }}>
                          <i className="fas fa-exclamation-circle me-1"></i>Preenchimento obrigatório
                        </div>
                      ) : conf?.resolucao ? (
                        <div style={{
                          padding: '3px 10px',
                          background: confirmado ? '#f0fff4' : '#fff5f5',
                          borderTop: `1px solid ${confirmado ? '#c3e6cb' : '#f5c6cb'}`,
                          fontSize: '0.70rem',
                          color: '#888',
                          textAlign: 'right',
                        }}>
                          {conf.resolucao.length} caracteres
                        </div>
                      ) : null}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const Auditoria_resultado = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auditoria = location.state?.auditoria;

  const [conferencias,         setConferencias]         = useState({});
  const [salvando,             setSalvando]             = useState(false);
  const [msgSalvar,            setMsgSalvar]            = useState(null);
  const [observacoesManuais,   setObservacoesManuais]   = useState([]);
  const [salvoComSucesso,      setSalvoComSucesso]      = useState(false);
  const [competenciaEntrada,   setCompetenciaEntrada]   = useState('');
  const [competenciaSaida,     setCompetenciaSaida]     = useState('');
  const [competenciasOpcoes,   setCompetenciasOpcoes]   = useState([]);

  // Gera opções e pré-preenche entrada/saída a partir do período da auditoria
  // (auditoria.periodo = "dd/mm/aaaa a dd/mm/aaaa")
  useEffect(() => {
    if (!auditoria?.periodo) return;
    try {
      const [iniStr, fimStr] = auditoria.periodo.split(' a ');
      const parse = (s) => {
        const p = (s || '').trim().split('/'); // dd/mm/aaaa
        if (p.length < 3) return null;
        return { mes: parseInt(p[1]), ano: parseInt(p[2]) };
      };
      const ini = parse(iniStr);
      const fim = parse(fimStr) || ini;
      if (!ini) return;

      // Opções: de (entrada − 5 meses) até 12 meses à frente do maior entre
      // (fim do período auditado) e (mês atual) — assim sempre dá pra escolher
      // competências futuras, mesmo auditando um período antigo.
      let m = ini.mes - 5, a = ini.ano;
      while (m <= 0) { m += 12; a--; }
      let curIdx = a * 12 + (m - 1);
      const hoje = new Date();
      const fimIdx   = fim.ano * 12 + (fim.mes - 1);
      const hojeIdx  = hoje.getFullYear() * 12 + hoje.getMonth();
      const endIdx = Math.max(fimIdx, hojeIdx) + 12;
      const opcoes = [];
      while (curIdx <= endIdx) {
        const mm = (curIdx % 12) + 1;
        const yy = Math.floor(curIdx / 12);
        opcoes.push(`${String(mm).padStart(2, '0')}/${String(yy).slice(-2)}`);
        curIdx++;
      }
      setCompetenciasOpcoes(opcoes);
      setCompetenciaEntrada(`${String(ini.mes).padStart(2, '0')}/${String(ini.ano).slice(-2)}`);
      setCompetenciaSaida(`${String(fim.mes).padStart(2, '0')}/${String(fim.ano).slice(-2)}`);
    } catch {}
  }, [auditoria?.periodo]);

  const adicionarObservacao = () =>
    setObservacoesManuais(prev => [...prev, { severidade: 'BAIXO', categoria: '', conta: '', valor: '', descricao: '' }]);

  const atualizarObservacao = (idx, campo, val) =>
    setObservacoesManuais(prev => prev.map((o, i) => i === idx ? { ...o, [campo]: val } : o));

  const removerObservacao = (idx) =>
    setObservacoesManuais(prev => prev.filter((_, i) => i !== idx));

  const handleConferencia = (key, resposta) => {
    setConferencias(prev => ({
      ...prev,
      [key]: { ...prev[key], resposta, resolucao: prev[key]?.resolucao || '' },
    }));
  };

  const handleResolucao = (key, resolucao) => {
    setConferencias(prev => ({
      ...prev,
      [key]: { ...prev[key], resolucao },
    }));
  };

  if (!auditoria) {
    return (
      <div className="das-page">
        <div className="container-fluid mt-4">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <i className="fas fa-exclamation-triangle fa-4x text-warning mb-4" style={{ opacity: 0.5 }}></i>
              <h5 className="text-muted">Nenhum resultado de auditoria encontrado</h5>
              <p className="text-muted mb-4">Faça o upload dos arquivos e execute a auditoria primeiro.</p>
              <button className="btn btn-primary" onClick={() => navigate('/auditoria-balancete')}>
                <i className="fas fa-arrow-left me-2"></i>Voltar para Auditoria
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (salvoComSucesso) {
    return (
      <div className="das-page">
        <div className="container-fluid mt-4">
          <div className="card border-0 shadow-sm" style={{ borderRadius: 16, overflow: 'hidden' }}>
            <div className="card-body text-center py-5 px-4">
              <div style={{
                width: 80, height: 80, borderRadius: '50%',
                background: 'linear-gradient(135deg, #198754, #157347)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 24px',
                boxShadow: '0 8px 24px rgba(25,135,84,0.30)',
              }}>
                <i className="fas fa-check" style={{ color: '#fff', fontSize: '2rem' }}></i>
              </div>
              <h3 className="fw-bold text-success mb-2">Auditoria Salva!</h3>
              <p className="text-muted mb-4">Os dados foram registrados com sucesso no histórico de auditorias.</p>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/auditoria-balancete')}
                >
                  <i className="fas fa-plus me-2"></i>Nova Auditoria
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => navigate('/historico-auditoria')}
                >
                  <i className="fas fa-history me-2"></i>Ver Histórico
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { empresa, periodo, regime, equacao_contabil, resumo, divergencias, indicadores, notificacoes, checklist_resumo, metadata, _aviso } = auditoria;
  const graves = divergencias?.filter(d => normSev(d.severidade) === 'GRAVE') || [];
  const medios  = divergencias?.filter(d => normSev(d.severidade) === 'MEDIO') || [];
  const baixos  = divergencias?.filter(d => normSev(d.severidade) === 'BAIXO') || [];

  // Conferência obrigatória: toda divergência da IA precisa ser Confirmada/Contestada
  // E ter o plano de resolução / observação (ou motivo) preenchido.
  const confOk = (key) => {
    const c = conferencias[key];
    return !!(c?.resposta && (c.resolucao || '').trim());
  };
  const totalDivergencias  = graves.length + medios.length + baixos.length;
  const conferenciasFeitas =
    graves.filter((_, i) => confOk(`grave_${i}`)).length +
    medios.filter((_, i) => confOk(`medio_${i}`)).length +
    baixos.filter((_, i) => confOk(`baixo_${i}`)).length;
  const conferenciasPendentes = totalDivergencias - conferenciasFeitas;
  const conferenciaCompleta   = conferenciasPendentes === 0;

  const buildItens = (rows, prefix) => rows.map((d, i) => {
    const conf = conferencias[`${prefix}_${i}`];
    return {
      item_checklist:  d.item_checklist,
      severidade:      d.severidade,
      categoria:       d.categoria       || '',
      conta:           d.conta           || '',
      codigo:          d.codigo          || '',
      valor:           d.valor           ?? null,
      descricao:       d.descricao       || '',
      recomendacao:    d.recomendacao    || '',
      conferencia:     conf?.resposta    || null,
      plano_resolucao: conf?.resolucao   || '',
      resolvido:       conf?.resposta === 'Contestado' ? 'Sim' : 'Nao',
    };
  });

  const handleSalvar = async () => {
    if (!divergencias || divergencias.length === 0) return;
    if (!conferenciaCompleta) {
      setMsgSalvar({ tipo: 'warning', texto: `Confira e preencha o plano de resolução / observação de todas as divergências antes de salvar — faltam ${conferenciasPendentes} de ${totalDivergencias}.` });
      return;
    }
    setSalvando(true);
    setMsgSalvar(null);
    try {
      const token = localStorage.getItem('access');
      const payload = {
        empresa,
        numero_dominio: String(metadata?.numero_dominio ?? ''),
        periodo,
        regime,
        capital_de_giro: indicadores?.capital_de_giro ?? null,
        itens: [
          ...buildItens(graves, 'grave'),
          ...buildItens(medios, 'medio'),
          ...buildItens(baixos, 'baixo'),
          ...observacoesManuais.filter(o => o.descricao.trim()).map(o => ({
            item_checklist:  'M',
            severidade:      o.severidade,
            categoria:       o.categoria || 'Manual',
            conta:           o.conta     || '',
            valor:           o.valor     ? parseFloat(o.valor) : null,
            descricao:       o.descricao,
            recomendacao:    '',
            conferencia:     'Confirmado',
            plano_resolucao: '',
          })),
        ],
      };
      await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/salvar-historico-auditoria/`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Registra a competência de saída (a mais recente do intervalo auditado)
      if (competenciaSaida && metadata?.numero_dominio) {
        const [mm, yy] = competenciaSaida.split('/');
        await axios.post(
          `${process.env.REACT_APP_API_BASE}/api/registrar-auditoria-realizada/`,
          { numero_dominio: String(metadata.numero_dominio), data_auditoria: `01/${mm}/20${yy}` },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      setSalvoComSucesso(true);
    } catch (err) {
      const detalhe = err.response?.data?.erro || err.message;
      setMsgSalvar({ tipo: 'danger', texto: `Erro ao salvar: ${detalhe}` });
    } finally {
      setSalvando(false);
    }
  };
  const totalProblemas = graves.length + medios.length + baixos.length;
  const statusGeral = graves.length > 0 ? 'CRÍTICO' : medios.length > 0 ? 'ATENÇÃO' : 'APROVADO';
  const statusColor = { CRÍTICO: '#dc3545', ATENÇÃO: '#ffc107', APROVADO: '#198754' };

  return (
    <div className="das-page">
      <style>{PRINT_STYLE}</style>
      <div className="container-fluid mt-4">

        {/* ===== AVISO DE TRUNCAMENTO ===== */}
        {_aviso && (
          <div className="alert alert-warning d-flex align-items-center gap-2 mb-3 border-0 shadow-sm">
            <i className="fas fa-exclamation-triangle fa-lg text-warning flex-shrink-0"></i>
            <div>
              <strong>Atenção:</strong> {_aviso}. Os dados exibidos abaixo são o que foi possível recuperar — alguns itens do checklist podem estar ausentes.
            </div>
          </div>
        )}

        {/* ===== HEADER ===== */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="mb-4">
                      <span className="badge bg-light text-dark me-2" style={{ fontSize: '20px', fontWeight: '500' }}>
                        Resultado da Auditoria Contábil
                      </span>
                      <span
                        className="badge ms-1"
                        style={{ background: statusColor[statusGeral], fontSize: '15px', fontWeight: '700', padding: '5px 12px' }}
                      >
                        <i className={`fas ${statusGeral === 'APROVADO' ? 'fa-check-circle' : statusGeral === 'CRÍTICO' ? 'fa-times-circle' : 'fa-exclamation-circle'} me-1`}></i>
                        {statusGeral}
                      </span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <i className="fas fa-shield-alt header-icon me-2"></i>
                      <h2 className="titulo-pagina m-0">{empresa || 'Empresa não identificada'}</h2>
                    </div>
                    <p className="mb-1 opacity-75">
                      <i className="fas fa-calendar-alt me-2"></i>{periodo}
                      <span className="ms-3"><i className="fas fa-file-invoice-dollar me-2"></i>{regime}</span>
                    </p>
                  </div>
                  <div className="d-flex flex-column gap-2 align-items-end no-print">
                    <button
                      className="btn btn-sm"
                      onClick={() => navigate('/auditoria-balancete')}
                      style={{ backgroundColor: '#00205b', borderColor: '#00205b', color: '#fff' }}
                    >
                      <i className="fas fa-arrow-left me-1"></i>Nova Auditoria
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={() => window.print()}
                      style={{ backgroundColor: '#198754', borderColor: '#198754', color: '#fff' }}
                    >
                      <i className="fas fa-file-pdf me-1"></i>Imprimir PDF
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

    {/* ===== EQUAÇÃO CONTÁBIL ===== */}
        {equacao_contabil && (
          <div className="row mb-4">
            <div className="col-12">
              <div className={`alert ${equacao_contabil.fechou ? 'alert-success' : 'alert-danger'} border-0 shadow-sm d-flex align-items-center gap-3`} style={{ borderRadius: 12 }}>
                <i className={`fas ${equacao_contabil.fechou ? 'fa-check-circle fa-2x' : 'fa-times-circle fa-2x'}`}></i>
                <div className="flex-grow-1">
                  <strong>
                    Integridade do Balancete
                    {equacao_contabil.estagio === 'pre_encerramento'
                      ? ' — pré-encerramento (contas de resultado abertas)'
                      : ' — pós-encerramento'}
                  </strong>
                  <div className="mt-1 d-flex flex-wrap gap-3">
                    <span>Ativo: <strong>R$ {fmtCurrency(equacao_contabil.ativo)}</strong></span>
                    <span>Passivo + PL: <strong>R$ {fmtCurrency(equacao_contabil.passivo_mais_pl)}</strong></span>
                    {equacao_contabil.estagio === 'pre_encerramento' && equacao_contabil.resultado_periodo != null && (
                      <span>
                        Resultado do período:&nbsp;
                        <strong className={equacao_contabil.resultado_periodo >= 0 ? 'text-success' : 'text-danger'}>
                          R$ {fmtCurrency(equacao_contabil.resultado_periodo)}
                          {equacao_contabil.resultado_periodo >= 0 ? ' (lucro)' : ' (prejuízo)'}
                        </strong>
                      </span>
                    )}
                    <span>
                      Resíduo de fechamento:&nbsp;
                      <strong>R$ {fmtCurrency(equacao_contabil.diferenca)}</strong>
                    </span>
                    <span className={`badge ${equacao_contabil.fechou ? 'bg-success' : 'bg-danger'} fs-6`}>
                      {equacao_contabil.fechou
                        ? <>Balancete fecha <i className="fas fa-check-circle ms-1"></i></>
                        : 'Balancete NÃO fecha — ERRO GRAVE'}
                    </span>
                  </div>
                  {equacao_contabil.estagio === 'pre_encerramento' && (
                    <small className="d-block mt-1 opacity-75">
                      Ativo − (Passivo+PL) = R$ {fmtCurrency(equacao_contabil.resultado_periodo)} = resultado ainda não transferido ao PL. A igualdade Ativo = Passivo+PL só vale após o encerramento.
                    </small>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== KPIs ===== */}
        <div className="row g-3 mb-4">
          {[
            { label: 'Graves',          value: graves.length,        icon: 'fa-shield-alt',    color: '#dc3545', bg: '#fff1f2', ring: '#fecdd3' },
            { label: 'Médios',          value: medios.length,        icon: 'fa-exclamation',   color: '#d97706', bg: '#fffbeb', ring: '#fde68a' },
            { label: 'Baixos',          value: baixos.length,        icon: 'fa-flag',          color: '#0284c7', bg: '#f0f9ff', ring: '#bae6fd' },
            { label: 'Total',           value: totalProblemas,       icon: 'fa-list-alt',      color: '#6f42c1', bg: '#faf5ff', ring: '#ddd6fe' },
          ].map((k, i) => (
            <div key={i} className="col-6 col-md-3">
              <div className="card border-0 shadow-sm h-100" style={{ background: k.bg }}>
                <div className="card-body py-4 px-3 d-flex align-items-center gap-3">
                  <div style={{
                    width: 52, height: 52, borderRadius: '14px',
                    background: '#fff',
                    border: `1.5px solid ${k.ring}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: `0 2px 8px ${k.ring}88`,
                  }}>
                    <i className={`fas ${k.icon}`} style={{ fontSize: '1.25rem', color: k.color }}></i>
                  </div>
                  <div>
                    <div style={{ fontSize: '1.90rem', fontWeight: 700, lineHeight: 1, color: k.color }}>{k.value}</div>
                    <div style={{ fontSize: '0.80rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>{k.label}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ===== CHECKLIST DE AUDITORIA ===== */}
        {checklist_resumo && checklist_resumo.length > 0 && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-clipboard-check me-2 text-primary"></i>
                    Checklist de Auditoria — {checklist_resumo.length} Itens Verificados
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    {checklist_resumo.map((item) => {
                      const temDivergencia = divergencias?.some(d => String(d.item_checklist) === String(item.item));
                      const sevMax = temDivergencia
                        ? divergencias.filter(d => String(d.item_checklist) === String(item.item))
                            .reduce((max, d) => {
                              const ord = { GRAVE: 3, MEDIO: 2, BAIXO: 1 };
                              const sev = normSev(d.severidade);
                              return (ord[sev] || 0) > (ord[max] || 0) ? sev : max;
                            }, '')
                        : null;

                      const cfg = item.status === 'OK' || (!temDivergencia && item.status !== 'NAO_VERIFICAVEL')
                        ? { icon: 'fa-check-circle', color: '#198754', bg: '#f0fff4', border: '#c3e6cb', badge: 'bg-success', label: 'Conferido' }
                        : item.status === 'NAO_VERIFICAVEL'
                        ? { icon: 'fa-question-circle', color: '#6c757d', bg: '#f8f9fa', border: '#dee2e6', badge: 'bg-secondary', label: 'Não Verificável' }
                        : sevMax === 'GRAVE'
                        ? { icon: 'fa-times-circle', color: '#dc3545', bg: '#fff5f5', border: '#f5c6cb', badge: 'bg-danger', label: 'GRAVE' }
                        : sevMax === 'MEDIO'
                        ? { icon: 'fa-exclamation-triangle', color: '#856404', bg: '#fffbea', border: '#ffeeba', badge: 'bg-warning text-dark', label: 'MÉDIO' }
                        : { icon: 'fa-info-circle', color: '#0c5460', bg: '#f0faff', border: '#bee5eb', badge: 'bg-info text-dark', label: 'BAIXO' };

                      return (
                        <div key={item.item} className="col-md-6 col-xl-4">
                          <div className="p-3 rounded h-100" style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}>
                            <div className="d-flex align-items-start gap-2">
                              <i className={`fas ${cfg.icon} mt-1 flex-shrink-0`} style={{ color: cfg.color, fontSize: '1.15rem' }}></i>
                              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                  <span className="fw-bold text-dark" style={{ fontSize: '0.92rem' }}>
                                    {item.item}. {item.titulo}
                                  </span>
                                  <span className={`badge ${cfg.badge}`} style={{ fontSize: '11px' }}>{cfg.label}</span>
                                </div>
                                <p className="mb-0 text-muted" style={{ fontSize: '0.87rem', lineHeight: 1.4 }}>
                                  {item.resumo || '—'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== INDICADORES ===== */}
        {indicadores && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-chart-bar me-2 text-primary"></i>Indicadores Financeiros
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-4 align-items-center">
                    <div className="col-md-3 text-center border-end">
                      <small className="text-muted text-uppercase fw-semibold d-block mb-1">Capital de Giro</small>
                      <h4 className={`fw-bold mb-1 ${(indicadores.capital_de_giro || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                        R$ {fmtCurrency(indicadores.capital_de_giro)}
                      </h4>
                      <small className={`badge ${(indicadores.capital_de_giro || 0) >= 0 ? 'bg-success' : 'bg-danger'}`}>
                        {(indicadores.capital_de_giro || 0) >= 0 ? 'Positivo' : 'Negativo'}
                      </small>
                    </div>
                    <div className="col-md-3 text-center border-end">
                      <small className="text-muted text-uppercase fw-semibold d-block mb-1">Grau de Endividamento</small>
                      <h4 className="fw-bold mb-1 text-primary">{fmtCurrency(indicadores.grau_endividamento)}%</h4>
                      <small className={`badge ${(indicadores.grau_endividamento || 0) > 70 ? 'bg-danger' : (indicadores.grau_endividamento || 0) > 40 ? 'bg-warning text-dark' : 'bg-success'}`}>
                        {(indicadores.grau_endividamento || 0) > 70 ? 'Alto' : (indicadores.grau_endividamento || 0) > 40 ? 'Moderado' : 'Saudável'}
                      </small>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted text-uppercase fw-semibold d-block mb-1">Observações da IA</small>
                      <p className="mb-0">{indicadores.observacoes || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== DIVERGÊNCIAS GRAVES ===== */}
        {graves.length > 0 && (
          <div className="row mb-3">
            <div className="col-12">
              <div className="card border-0 shadow-sm" style={{ borderLeft: '3px solid #dc3545' }}>
                <div className="card-header border-bottom d-flex align-items-center gap-2 py-2" style={{ background: '#fce8e8' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#dc3545', display: 'inline-block', flexShrink: 0 }}></span>
                  <span className="fw-semibold text-dark" style={{ fontSize: '1rem' }}>Alertas Graves</span>
                  <span className="badge rounded-pill ms-1" style={{ background: '#fce8e8', color: '#b91c1c', fontSize: '0.8rem' }}>{graves.length}</span>
                </div>
                <div className="card-body p-0">
                  <DivergenciasTable rows={graves} prefix="grave" conferencias={conferencias} onConferencia={handleConferencia} onResolucao={handleResolucao} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== DIVERGÊNCIAS MÉDIAS ===== */}
        {medios.length > 0 && (
          <div className="row mb-3">
            <div className="col-12">
              <div className="card border-0 shadow-sm" style={{ borderLeft: '3px solid #d97706' }}>
                <div className="card-header border-bottom d-flex align-items-center gap-2 py-2" style={{ background: '#fef3c7' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#d97706', display: 'inline-block', flexShrink: 0 }}></span>
                  <span className="fw-semibold text-dark" style={{ fontSize: '1rem' }}>Alertas Médios</span>
                  <span className="badge rounded-pill ms-1" style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.8rem' }}>{medios.length}</span>
                </div>
                <div className="card-body p-0">
                  <DivergenciasTable rows={medios} prefix="medio" conferencias={conferencias} onConferencia={handleConferencia} onResolucao={handleResolucao} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== PONTOS DE ATENÇÃO ===== */}
        {baixos.length > 0 && (
          <div className="row mb-3">
            <div className="col-12">
              <div className="card border-0 shadow-sm" style={{ borderLeft: '3px solid #0284c7' }}>
                <div className="card-header border-bottom d-flex align-items-center gap-2 py-2" style={{ background: '#e0f2fe' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#0284c7', display: 'inline-block', flexShrink: 0 }}></span>
                  <span className="fw-semibold text-dark" style={{ fontSize: '1rem' }}>Pontos de Atenção</span>
                  <span className="badge rounded-pill ms-1" style={{ background: '#e0f2fe', color: '#075985', fontSize: '0.8rem' }}>{baixos.length}</span>
                </div>
                <div className="card-body p-0">
                  <DivergenciasTable rows={baixos} prefix="baixo" conferencias={conferencias} onConferencia={handleConferencia} onResolucao={handleResolucao} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== NOTIFICAÇÕES MANUAIS ===== */}
        {notificacoes && notificacoes.length > 0 && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-light border-bottom">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-bell me-2 text-secondary"></i>
                    Notificações — Verificação Manual Necessária
                    <span className="badge bg-secondary ms-2">{notificacoes.length}</span>
                  </h5>
                </div>
                <div className="card-body">
                  <ul className="mb-0">
                    {notificacoes.map((n, i) => (
                      <li key={i} className="mb-2">
                        <i className="fas fa-angle-right me-2 text-muted"></i>{n}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== NENHUMA DIVERGÊNCIA ===== */}
        {divergencias?.length === 0 && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5">
                  <i className="fas fa-check-circle fa-4x text-success mb-3"></i>
                  <h5 className="text-success">Nenhum alerta encontrado</h5>
                  <p className="text-muted mb-0">A auditoria não identificou nenhum ponto de atenção nos documentos analisados.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== AVISO TRUNCAMENTO ===== */}
        {auditoria._aviso && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="alert alert-warning">
                <i className="fas fa-exclamation-triangle me-2"></i>{auditoria._aviso}
              </div>
            </div>
          </div>
        )}

        {/* ===== RODAPÉ METADADOS ===== */}
        {metadata && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm bg-light">
                <div className="card-body py-2 px-3 d-flex flex-wrap gap-3 align-items-center" style={{ fontSize: '0.86rem', color: '#6c757d' }}>
                  <span><i className="fas fa-robot me-1"></i><strong>Modelo:</strong> {metadata.modelo_ia}</span>
                  <span><i className="fas fa-list me-1"></i><strong>Contas:</strong> {metadata.total_contas_balancete}</span>
                  <span><i className="fas fa-receipt me-1"></i><strong>Lançamentos:</strong> {metadata.total_lancamentos_razao}</span>
                  <span><i className="fas fa-clock me-1"></i><strong>Analisado em:</strong> {metadata.analisado_em ? new Date(metadata.analisado_em).toLocaleString('pt-BR') : '-'}</span>
                  <span><i className="fas fa-file-invoice-dollar me-1"></i><strong>Regime:</strong> {metadata.regime}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== AUDITORIA MANUAL ===== */}
        <div className="row mb-4 no-print">
          <div className="col-12">
            <div className="card border-0 shadow-sm" style={{ borderRadius: 12, overflow: 'hidden' }}>

              {/* Header */}
              <div className="d-flex align-items-center justify-content-between px-4 py-3"
                style={{ background: 'linear-gradient(135deg, #1a3a6b 0%, #2d6a9f 100%)' }}>
                <div className="d-flex align-items-center gap-3">
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="fas fa-pencil-alt" style={{ color: '#fff', fontSize: '1rem' }}></i>
                  </div>
                  <div>
                    <div className="fw-bold text-white" style={{ fontSize: '1rem' }}>Observações Manuais</div>
                    <small style={{ color: 'rgba(255,255,255,0.70)', fontSize: '0.86rem' }}>Registre itens que a IA não detectou mas você identificou na auditoria</small>
                  </div>
                </div>
                <button className="btn btn-sm fw-semibold" onClick={adicionarObservacao}
                  style={{ background: '#fff', color: '#1a3a6b', border: 'none', borderRadius: 8, padding: '6px 16px' }}>
                  <i className="fas fa-plus me-1"></i>Adicionar Item
                </button>
              </div>

              {observacoesManuais.length === 0 ? (
                <div className="text-center py-5" style={{ background: '#f8faff' }}>
                  <i className="fas fa-clipboard-list fa-3x mb-3" style={{ color: '#c5d4f0', opacity: 0.7 }}></i>
                  <div className="text-muted fw-semibold mb-1">Nenhuma observação adicionada</div>
                  <small className="text-muted">Clique em <strong>Adicionar Item</strong> para registrar um achado manual</small>
                </div>
              ) : (
                <div style={{ background: '#f8faff' }}>
                  {observacoesManuais.map((o, idx) => {
                    const sevColor = o.severidade === 'GRAVE' ? '#dc3545' : o.severidade === 'MEDIO' ? '#d97706' : '#0284c7';
                    const sevBg    = o.severidade === 'GRAVE' ? '#fff1f2' : o.severidade === 'MEDIO' ? '#fffbeb' : '#f0f9ff';
                    return (
                      <div key={idx} style={{ background: '#fff', margin: '12px 16px', borderRadius: 10, border: `1.5px solid ${sevColor}33`, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                        {/* Linha de topo colorida + número */}
                        <div className="d-flex align-items-center justify-content-between px-3 py-2"
                          style={{ background: sevBg, borderRadius: '8px 8px 0 0', borderBottom: `1px solid ${sevColor}22` }}>
                          <div className="d-flex align-items-center gap-2">
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: sevColor, display: 'inline-block', flexShrink: 0 }}></span>
                            <span className="fw-semibold" style={{ fontSize: '0.82rem', color: sevColor }}>Observação #{idx + 1}</span>
                          </div>
                          <button className="btn btn-sm" style={{ color: '#dc3545', background: 'transparent', border: 'none', padding: '0 4px' }}
                            onClick={() => removerObservacao(idx)} title="Remover">
                            <i className="fas fa-times"></i>
                          </button>
                        </div>

                        {/* Campos */}
                        <div className="p-3">
                          <div className="row g-2 mb-2">
                            <div className="col-md-2">
                              <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.83rem', color: '#555' }}>SEVERIDADE</label>
                              <select className="form-select form-select-sm" value={o.severidade}
                                onChange={e => atualizarObservacao(idx, 'severidade', e.target.value)}
                                style={{ fontSize: '0.82rem', borderColor: sevColor, color: sevColor, fontWeight: 600 }}>
                                <option value="GRAVE">Grave</option>
                                <option value="MEDIO">Médio</option>
                                <option value="BAIXO">Baixo</option>
                              </select>
                            </div>
                            <div className="col-md-3">
                              <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.83rem', color: '#555' }}>CATEGORIA</label>
                              <input type="text" className="form-control form-control-sm" placeholder="ex: Patrimônio Líquido"
                                value={o.categoria} onChange={e => atualizarObservacao(idx, 'categoria', e.target.value)} />
                            </div>
                            <div className="col-md-4">
                              <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.83rem', color: '#555' }}>CONTA</label>
                              <input type="text" className="form-control form-control-sm" placeholder="Nome da conta contábil"
                                value={o.conta} onChange={e => atualizarObservacao(idx, 'conta', e.target.value)} />
                            </div>
                            <div className="col-md-3">
                              <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.83rem', color: '#555' }}>VALOR (R$)</label>
                              <input type="number" className="form-control form-control-sm" placeholder="0,00"
                                value={o.valor} onChange={e => atualizarObservacao(idx, 'valor', e.target.value)} />
                            </div>
                          </div>
                          <div>
                            <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.83rem', color: '#555' }}>DESCRIÇÃO DO ACHADO</label>
                            <textarea className="form-control" rows={2}
                              placeholder="Descreva detalhadamente o que você observou..."
                              style={{ resize: 'vertical', fontSize: '0.95rem', lineHeight: 1.55 }}
                              value={o.descricao} onChange={e => atualizarObservacao(idx, 'descricao', e.target.value)} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ height: 12 }}></div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ===== BOTÃO SALVAR ===== */}
        <div className="row mb-4 no-print">
          <div className="col-12 d-flex flex-column align-items-end gap-2">
            {msgSalvar && (
              <div className={`alert alert-${msgSalvar.tipo} alert-dismissible d-flex align-items-center gap-2 w-100 border-0 shadow-sm`}>
                <i className={`fas fa-exclamation-circle fa-lg flex-shrink-0`}></i>
                <div className="flex-grow-1">{msgSalvar.texto}</div>
                <button type="button" className="btn-close" onClick={() => setMsgSalvar(null)}></button>
              </div>
            )}
            {totalDivergencias > 0 && (
              <small className={`fw-semibold ${conferenciaCompleta ? 'text-success' : 'text-danger'}`} style={{ whiteSpace: 'nowrap' }}>
                <i className={`fas ${conferenciaCompleta ? 'fa-check-circle' : 'fa-exclamation-circle'} me-1`}></i>
                {conferenciaCompleta
                  ? `Todas as ${totalDivergencias} divergências conferidas e justificadas`
                  : `Conferência + plano/observação obrigatórios: ${conferenciasFeitas}/${totalDivergencias} completos — faltam ${conferenciasPendentes}`}
              </small>
            )}
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0 fw-semibold text-muted" style={{ fontSize: '0.93rem', whiteSpace: 'nowrap' }}>
                <i className="fas fa-calendar-alt me-1"></i>Competência auditada:
              </label>
              <select
                className="form-select form-select-sm"
                style={{ width: 110 }}
                value={competenciaEntrada}
                onChange={e => setCompetenciaEntrada(e.target.value)}
                title="Competência de entrada (inicial)"
              >
                {competenciasOpcoes.length === 0 && <option value="">—</option>}
                {competenciasOpcoes.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <span className="text-muted" style={{ fontSize: '0.85rem' }}>até</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 110 }}
                value={competenciaSaida}
                onChange={e => setCompetenciaSaida(e.target.value)}
                title="Competência de saída (final)"
              >
                {competenciasOpcoes.length === 0 && <option value="">—</option>}
                {competenciasOpcoes.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <button
                className="btn btn-sm"
                onClick={handleSalvar}
                disabled={salvando || (!divergencias?.length && !observacoesManuais.length) || !conferenciaCompleta}
                title={!conferenciaCompleta ? 'Faça a conferência de todas as divergências antes de salvar' : 'Salvar auditoria'}
                style={{ backgroundColor: '#198754', borderColor: '#198754', color: '#fff' }}
              >
                {salvando
                  ? <><span className="spinner-border spinner-border-sm me-1"></span>Salvando...</>
                  : <><i className="fas fa-save me-1"></i>Salvar Auditoria</>}
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Auditoria_resultado;
